'use client';

import { useTenant } from '@/contexts/TenantContext';
import { analyticsEvents } from '@/components/analytics/GoogleAnalytics';
import { WhatsAppWidget } from '@routeroyal/ui-core';
import type { WhatsAppConfig } from '@/lib/api/types';

/**
 * Hardcoded WhatsApp defaults per theme.
 * These are used when the Route Royal API does not yet provide a whatsapp config.
 * Once the API returns a config, it takes precedence over these defaults.
 */
const THEME_WHATSAPP_DEFAULTS: Record<string, WhatsAppConfig> = {
  allys: {
    enabled: true,
    defaultMessage: "Hi, I'm interested in your properties in Mauritius.",
    position: 'bottom-right',
    showOnMobile: true,
    agents: [
      {
        name: 'Yalda Sheri',
        phoneNumber: '23052584239',
        department: 'Senior Property Consultant',
        avatar: 'https://res.cloudinary.com/elementary-media/image/upload/v1774846899/WhatsApp_Image_2026-03-20_at_12.31.58_rggoze-Photoroom_g2p2h5.png',
        availabilityText: 'Typically replies within 1 hour',
      },
      {
        name: 'Junaid Nuzeebun',
        phoneNumber: '23052584240',
        department: 'Property Development Specialist',
        avatar: 'https://res.cloudinary.com/elementary-media/image/upload/v1774844074/junaid-nuzeebun-profile-picture_aa1cb0.png',
        availabilityText: 'Typically replies within 1 hour',
      },
    ],
  },
  'stone-investment': {
    enabled: true,
    defaultMessage: "Bonjour, I'm interested in learning more about your properties.",
    position: 'bottom-right',
    showOnMobile: true,
    agents: [
      {
        name: 'Pierre Monville',
        phoneNumber: '33616641604',
        department: 'Founder & Managing Director',
        avatar: 'https://res.cloudinary.com/elementary-media/image/upload/v1769757358/pierre-2-mini_wwdcpn.jpg',
        availabilityText: 'Typically replies within a few hours',
      },
    ],
  },
  'stone-investments': {
    enabled: true,
    defaultMessage: "Bonjour, I'm interested in learning more about your properties.",
    position: 'bottom-right',
    showOnMobile: true,
    agents: [
      {
        name: 'Pierre Monville',
        phoneNumber: '33616641604',
        department: 'Founder & Managing Director',
        avatar: 'https://res.cloudinary.com/elementary-media/image/upload/v1769757358/pierre-2-mini_wwdcpn.jpg',
        availabilityText: 'Typically replies within a few hours',
      },
    ],
  },
};

/**
 * WhatsApp Chat Widget - Tenant-aware wrapper
 *
 * Reads WhatsApp configuration from the tenant context and renders
 * the core WhatsAppWidget with analytics integration.
 * Falls back to hardcoded theme defaults when the API config is not yet available.
 */
export function WhatsAppChatWidget() {
  const { whatsapp, themeId } = useTenant();

  const config = whatsapp ?? THEME_WHATSAPP_DEFAULTS[themeId];

  if (!config?.enabled || !config.agents?.length) {
    return null;
  }

  const widgetAgents = config.agents.map((agent) => ({
    name: agent.name,
    phoneNumber: agent.phoneNumber,
    department: agent.department,
    avatar: agent.avatar,
    availabilityText: agent.availabilityText,
  }));

  return (
    <WhatsAppWidget
      agents={widgetAgents}
      defaultMessage={config.defaultMessage}
      position={config.position}
      onClickAgent={(phoneNumber, agentName) => {
        analyticsEvents.clickToWhatsApp(phoneNumber, `whatsapp_widget_${agentName}`);
      }}
    />
  );
}
